import React, { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, isBefore, isAfter, isSameMonth } from 'date-fns';
import { useAppContext } from '../context/AppContext';
import { getNormalizedMonthlyCost, toDate } from '../utils/dateUtils';
import SafeIcon from '../common/SafeIcon';
import { convertCurrency, formatCurrency } from '../utils/currencyUtils';

export default function Analytics() {
  const { state } = useAppContext();
  const [selectedMonth, setSelectedMonth] = useState(new Date());

  const analyticsData = useMemo(() => {
    let monthlyTotal = 0;
    const prefCurrency = state.settings.preferredCurrency || 'USD';
    
    let lifetimeTotal = state.receipts.reduce((acc, r) => {
      const sub = state.subscriptions.find(s => s.id === r.subscriptionId);
      const originalAmount = parseFloat(r.amount) || 0;
      const converted = convertCurrency(
        originalAmount, 
        sub?.currency || 'USD', 
        prefCurrency, 
        state.exchangeRates
      );
      return acc + converted;
    }, 0);

    const catMap = {};
    const subsCost = [];
    let countActive = 0;

    const monthStart = startOfMonth(selectedMonth);
    const monthEnd = endOfMonth(selectedMonth);

    state.subscriptions.forEach(sub => {
      const commencement = toDate(sub.commencementDate);
      const cancellation = sub.cancelledAt ? toDate(sub.cancelledAt) : null;

      const wasActiveInMonth = isBefore(commencement, monthEnd) && 
        (!cancellation || isAfter(cancellation, monthStart) || isSameMonth(cancellation, selectedMonth));

      if (wasActiveInMonth) {
        const monthlyCost = getNormalizedMonthlyCost(sub.recurringAmount, sub.recurrenceInterval);
        
        // Currency Conversion
        const convertedMonthly = convertCurrency(
          monthlyCost, 
          sub.currency || 'USD', 
          prefCurrency, 
          state.exchangeRates
        );

        monthlyTotal += convertedMonthly;

        if (!cancellation || isAfter(cancellation, monthEnd)) {
          countActive++;
        }

        const cat = state.categories.find(c => c.id === sub.categoryId)?.name || 'Uncategorized';
        catMap[cat] = (catMap[cat] || 0) + convertedMonthly;
        
        subsCost.push({ 
          name: sub.name, 
          cost: convertedMonthly, 
          status: sub.status 
        });
      }
    });

    const chartData = Object.keys(catMap).map(k => ({
      name: k,
      value: parseFloat(catMap[k].toFixed(2))
    }));

    const sortedSubs = subsCost.sort((a, b) => b.cost - a.cost).slice(0, 5);

    return {
      totalMonthly: monthlyTotal,
      lifetimeTotal,
      categoryData: chartData,
      topSubs: sortedSubs,
      activeCount: countActive
    };
  }, [state.subscriptions, state.categories, state.receipts, selectedMonth, state.settings.preferredCurrency, state.exchangeRates]);

  const { totalMonthly, lifetimeTotal, categoryData, topSubs, activeCount } = analyticsData;

  const pieOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        return `${params.name}: ${formatCurrency(params.value, state.settings.preferredCurrency)} (${params.percent}%)`;
      }
    },
    legend: {
      bottom: '0%',
      left: 'center',
      icon: 'circle',
      textStyle: { color: '#64748b' }
    },
    series: [
      {
        type: 'pie',
        radius: ['45%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 12,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: { show: false },
        data: categoryData,
        color: ['#0d9488', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981']
      }
    ]
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Financial Insights</h1>
          <p className="text-slate-500">Analysis for {format(selectedMonth, 'MMMM yyyy')} in {state.settings.preferredCurrency}</p>
        </div>
        <div className="flex items-center bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <button onClick={() => setSelectedMonth(subMonths(selectedMonth, 1))} className="p-2.5 hover:bg-slate-50 border-r border-slate-100 transition-colors">
            <SafeIcon name="ChevronLeft" className="w-5 h-5" />
          </button>
          <div className="px-6 py-2.5 font-bold text-slate-800 min-w-[150px] text-center">
            {format(selectedMonth, 'MMM yyyy')}
          </div>
          <button onClick={() => setSelectedMonth(addMonths(selectedMonth, 1))} className="p-2.5 hover:bg-slate-50 border-l border-slate-100 transition-colors">
            <SafeIcon name="ChevronRight" className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-slate-900 p-6 rounded-3xl text-white shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/10 rounded-full -mr-16 -mt-16 blur-3xl" />
          <h3 className="text-teal-400 font-bold uppercase tracking-widest text-[10px] mb-4">Monthly Burn Rate</h3>
          <p className="text-4xl font-bold mb-2">{formatCurrency(totalMonthly, state.settings.preferredCurrency)}</p>
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <SafeIcon name="Activity" className="w-4 h-4" />
            <span>{activeCount} active subscriptions</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <h3 className="text-slate-400 font-bold uppercase tracking-widest text-[10px] mb-4">Lifetime Logged</h3>
          <p className="text-4xl font-bold text-slate-800 mb-2">{formatCurrency(lifetimeTotal, state.settings.preferredCurrency)}</p>
          <div className="flex items-center gap-2 text-teal-600 text-sm font-medium">
            <SafeIcon name="FileText" className="w-4 h-4" />
            <span>{state.receipts.length} receipts processed</span>
          </div>
        </div>

        <div className="bg-teal-600 p-6 rounded-3xl text-white shadow-xl">
          <h3 className="text-teal-100 font-bold uppercase tracking-widest text-[10px] mb-4">Annual Forecast</h3>
          <p className="text-4xl font-bold mb-2">{formatCurrency(totalMonthly * 12, state.settings.preferredCurrency)}</p>
          <p className="text-teal-100/60 text-xs">Based on current monthly recurring cost</p>
        </div>

        <div className="md:col-span-2 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
            <SafeIcon name="PieChart" className="text-teal-500" /> Spending by Category ({state.settings.preferredCurrency})
          </h3>
          {categoryData.length > 0 ? (
            <ReactECharts option={pieOption} style={{ height: '300px' }} />
          ) : (
            <div className="h-[300px] flex flex-col items-center justify-center bg-slate-50 rounded-2xl border border-dashed border-slate-200 text-slate-400">
              <SafeIcon name="Inbox" className="w-12 h-12 mb-2 opacity-20" />
              <p>No spending data for this month</p>
            </div>
          )}
        </div>

        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
            <SafeIcon name="Award" className="text-teal-500" /> Top Expenses
          </h3>
          <div className="space-y-3">
            {topSubs.map((sub, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 transition-all hover:border-teal-200">
                <span className="font-medium text-slate-700 truncate mr-2">{sub.name}</span>
                <span className="font-bold text-slate-900">{formatCurrency(sub.cost, state.settings.preferredCurrency)}</span>
              </div>
            ))}
            {topSubs.length === 0 && <p className="text-center text-slate-400 py-10">No data</p>}
          </div>
        </div>
      </div>
    </div>
  );
}